'use client';

/**
 * Tri-state segmented control — Light / Dark / System. Ported from
 * ThemeMaker's {@code lms-portal/src/components/shell/ThemeToggle.tsx}.
 *
 * <p>Pairs with a bootstrap script that runs synchronously before paint
 * and applies the saved choice. The rules are:
 *
 * <pre>{@code
 *   localStorage 'lms-theme' value | data-theme on <html>
 *   ────────────────────────────── │ ─────────────────────
 *   'light'                         │ 'light'
 *   'dark'                          │ 'dark'
 *   'system' or absent              │ matches prefers-color-scheme
 * }</pre>
 *
 * <p>When the user picks {@code 'system'}, we listen for OS-preference
 * changes while the page is open and update {@code data-theme} live.
 * If they pick light or dark explicitly, we drop the listener.
 *
 * <p>Tenant-scoped storage key: the toggle assumes it's rendered under
 * {@code /[tenantSlug]/...} and namespaces the key as
 * {@code lms-theme:<slug>} so multiple tenants per origin don't share
 * a preference.
 */
import { useEffect, useRef, useState } from 'react';
import { useLocale, useMessage } from '../../portal';

type ThemePref = 'light' | 'dark' | 'system';

/** Built-in labels for the toggle's three options — bottom of the
 *  fallback chain below any host-supplied {@code theme.*} message (see
 *  {@code useMessage} below). Active locale → English fallback, same
 *  convention as {@code LiveSearchBox.tr()}. */
function tr(key: ThemePref | 'theme', locale: string): string {
  const dict: Record<string, Record<string, string>> = {
    theme: { tr: 'Tema', en: 'Theme' },
    light: { tr: 'Açık tema', en: 'Light theme' },
    dark: { tr: 'Koyu tema', en: 'Dark theme' },
    system: { tr: 'Sistem teması', en: 'System theme' },
  };
  return dict[key]?.[locale] ?? dict[key]?.['en'] ?? key;
}

export function ThemeToggle({
  variant = 'segmented',
}: {
  variant?: 'segmented' | 'dropdown' | 'classic' | 'cycle';
}) {
  const { locale } = useLocale();
  // Host-configured label (e.g. akademi-lms-v3's common.json `theme.*`
  // keys, bridged in shellMessages.ts) wins over the built-in default.
  const labelTheme = useMessage('theme.theme', tr('theme', locale));
  const labelLight = useMessage('theme.light', tr('light', locale));
  const labelDark = useMessage('theme.dark', tr('dark', locale));
  const labelSystem = useMessage('theme.system', tr('system', locale));
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!dropdownOpen) return;
    const onPointerDown = (e: PointerEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setDropdownOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [dropdownOpen]);

  // `pref` is the user's intent (saved). The actually-applied class
  // (light vs dark) is derived from pref + system preference. We only
  // store pref in state so the segmented control highlights the right
  // segment — System should look active even when dark is showing.
  const [pref, setPref] = useState<ThemePref>('system');

  const themeKey = 'lms-theme';

  useEffect(() => {
    try {
      const current = localStorage.getItem(themeKey);
      if (current === null) {
        for (const k of Object.keys(localStorage)) {
          if (k !== themeKey && k.startsWith('lms-theme:')) {
            const migrated = localStorage.getItem(k);
            if (migrated === 'light' || migrated === 'dark' || migrated === 'system') {
              localStorage.setItem(themeKey, migrated);
            }
            break;
          }
        }
      }
      for (const k of Object.keys(localStorage)) {
        if (k !== themeKey && k.startsWith('lms-theme:')) {
          localStorage.removeItem(k);
        }
      }
    } catch {
      /* localStorage blocked */
    }
  }, [themeKey]);

  useEffect(() => {
    const sync = () => {
      try {
        const rawSaved = localStorage.getItem(themeKey);
        let stored: ThemePref;
        if (rawSaved === 'light' || rawSaved === 'dark' || rawSaved === 'system') {
          stored = rawSaved;
        } else {
          const applied = document.documentElement.getAttribute('data-theme');
          stored = applied === 'dark' ? 'dark' : applied === 'light' ? 'light' : 'system';
        }
        setPref(stored);
      } catch {
        /* localStorage blocked */
      }
    };

    sync();

    window.addEventListener('lms-theme-change', sync);
    return () => window.removeEventListener('lms-theme-change', sync);
  }, [themeKey]);

  useEffect(() => {
    try {
      const rawSaved = localStorage.getItem(themeKey);
      if (rawSaved === 'system') {
        const mq = window.matchMedia('(prefers-color-scheme: dark)');
        const onChange = () => {
          document.documentElement.setAttribute('data-theme', mq.matches ? 'dark' : 'light');
        };
        onChange();
        mq.addEventListener('change', onChange);
        return () => mq.removeEventListener('change', onChange);
      }
    } catch {
      /* localStorage blocked */
    }
  }, [themeKey]);

  // Only needed to resolve the binary on/off state for the `classic`
  // switch when `pref === 'system'` — the segmented/dropdown variants
  // don't need this since they show all three states explicitly.
  const [prefersDark, setPrefersDark] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    setPrefersDark(mq.matches);
    const onChange = () => setPrefersDark(mq.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);
  const isDark = pref === 'dark' || (pref === 'system' && prefersDark);

  const choose = (next: ThemePref) => {
    setPref(next);
    try {
      localStorage.setItem(themeKey, next);
    } catch {
      /* private mode */
    }
    if (next === 'system') {
      const mq = window.matchMedia('(prefers-color-scheme: dark)');
      document.documentElement.setAttribute('data-theme', mq.matches ? 'dark' : 'light');
    } else {
      document.documentElement.setAttribute('data-theme', next);
    }
    window.dispatchEvent(new Event('lms-theme-change'));
  };

  if (variant === 'classic') {
    // Simple binary switch — no "system" segment. Toggling always picks
    // an explicit light/dark, same as clicking a segment in the
    // segmented variant.
    //
    // Visual: the knob is a hollow ring that sits over whichever side
    // is currently OFF (hiding that icon completely); the side that's
    // ON shows its icon plainly, uncovered. Light = orange ring/cream
    // fill parked over the moon slot; Dark = white hollow ring parked
    // over the sun slot.
    // Height matches the other header pills (NotificationsBell's 36px
    // circle, GamificationPointsBadge's 36px pill) so the cluster lines
    // up evenly.
    const trackWidth = 68;
    const trackHeight = 36;
    const trackPadding = 6;
    const knobSize = trackHeight - trackPadding * 2;
    const knobTravel = trackWidth - knobSize - trackPadding * 2;
    // Slight overshoot ("spring") easing so the knob eases past its
    // resting spot and settles back — a light bounce, not a full spring.
    const bounce = 'cubic-bezier(0.34, 1.56, 0.64, 1)';

    return (
      <button
        type="button"
        role="switch"
        aria-checked={isDark}
        aria-label={labelTheme}
        title={isDark ? labelDark : labelLight}
        onClick={() => choose(isDark ? 'light' : 'dark')}
        className="lms-theme-classic"
        style={{
          position: 'relative',
          width: trackWidth,
          height: trackHeight,
          padding: trackPadding,
          borderRadius: 999,
          cursor: 'pointer',
          background: isDark ? '#1e2530' : '#ffffff',
          border: isDark
            ? '1px solid var(--color-border-on-sunken, rgba(255,255,255,0.18))'
            : '1px solid var(--color-border-subtle, rgba(0,0,0,0.08))',
          transition: `background 200ms ease, transform 150ms ${bounce}`,
        }}
      >
        <style
          dangerouslySetInnerHTML={{
            __html: `
              .lms-theme-classic { transform: scale(1); }
              .lms-theme-classic:hover { transform: scale(1.04); }
              .lms-theme-classic:active { transform: scale(0.94); }
            `,
          }}
        />
        {/* Whichever side is ON shows its icon plainly. */}
        {!isDark && (
          <span
            aria-hidden
            style={{
              position: 'absolute',
              top: '50%',
              left: trackPadding + 4,
              transform: 'translateY(-50%)',
              color: '#f5a623',
              display: 'inline-flex',
            }}
          >
            <SunIcon size={17} filled />
          </span>
        )}
        {isDark && (
          <span
            aria-hidden
            style={{
              position: 'absolute',
              top: '50%',
              right: trackPadding + 4,
              transform: 'translateY(-50%)',
              color: '#ffffff',
              display: 'inline-flex',
            }}
          >
            <MoonIcon size={15} filled />
          </span>
        )}

        {/* Hollow-ring knob parked over the OFF side, hiding its icon. */}
        <span
          aria-hidden
          style={{
            position: 'relative',
            display: 'block',
            width: knobSize,
            height: knobSize,
            borderRadius: '50%',
            boxSizing: 'border-box',
            border: isDark ? '2.5px solid rgba(255,255,255,0.9)' : '3px solid #f5a623',
            background: isDark ? 'transparent' : '#fdecc8',
            transform: `translateX(${isDark ? 0 : knobTravel}px)`,
            transition: `transform 320ms ${bounce}, background 200ms ease, border-color 200ms ease`,
          }}
        />
      </button>
    );
  }

  if (variant === 'cycle') {
    // Same footprint as the other header pills (36px, matches
    // NotificationsBell / GamificationPointsBadge / classic's
    // trackHeight), but a single icon button instead of a track+knob —
    // a third state doesn't need extra width. Clicking steps through
    // Light → Dark → System and shows the icon for whichever is
    // current, so "which state am I in" and "what does clicking do"
    // are both readable from the one icon.
    const order: ThemePref[] = ['light', 'dark', 'system'];
    const next = order[(order.indexOf(pref) + 1) % order.length]!;
    const size = 36;
    const currentLabel = pref === 'light' ? labelLight : pref === 'dark' ? labelDark : labelSystem;

    const background =
      pref === 'light'
        ? '#ffffff'
        : pref === 'dark'
          ? '#1e2530'
          : 'var(--color-surface-page, #f3f4f6)';
    const border =
      pref === 'dark'
        ? '1px solid var(--color-border-on-sunken, rgba(255,255,255,0.18))'
        : '1px solid var(--color-border-subtle, rgba(0,0,0,0.08))';
    const iconColor =
      pref === 'light'
        ? '#f5a623'
        : pref === 'dark'
          ? '#ffffff'
          : 'var(--color-text-secondary, #4b5563)';

    return (
      <button
        type="button"
        aria-label={`${labelTheme}: ${currentLabel}`}
        title={currentLabel}
        onClick={() => choose(next)}
        className="lms-theme-cycle"
        style={{
          position: 'relative',
          width: size,
          height: size,
          borderRadius: '50%',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          background,
          border,
          color: iconColor,
          transition: 'background 200ms ease, transform 150ms cubic-bezier(0.34, 1.56, 0.64, 1)',
        }}
      >
        <style
          dangerouslySetInnerHTML={{
            __html: `
              .lms-theme-cycle { transform: scale(1); }
              .lms-theme-cycle:hover { transform: scale(1.04); }
              .lms-theme-cycle:active { transform: scale(0.94); }
            `,
          }}
        />
        {pref === 'light' && <SunIcon size={17} filled />}
        {pref === 'dark' && <MoonIcon size={15} filled />}
        {pref === 'system' && <MonitorIcon />}
      </button>
    );
  }

  if (variant === 'dropdown') {
    // Native <select><option> can't render an icon inside an option on
    // any browser, so the icon variant needs a real listbox — a button
    // trigger (current icon + label) plus an absolutely-positioned menu.
    const items: { value: ThemePref; label: string; icon: React.ReactNode }[] = [
      { value: 'light', label: labelLight, icon: <SunIcon /> },
      { value: 'dark', label: labelDark, icon: <MoonIcon /> },
      { value: 'system', label: labelSystem, icon: <MonitorIcon /> },
    ];
    const activeItem = items.find((i) => i.value === pref) ?? items[0]!;

    return (
      <div ref={dropdownRef} style={{ position: 'relative', display: 'inline-block' }}>
        <button
          type="button"
          aria-haspopup="listbox"
          aria-expanded={dropdownOpen}
          aria-label={labelTheme}
          onClick={() => setDropdownOpen((v) => !v)}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            padding: '4px 10px',
            fontSize: 13,
            lineHeight: 1.4,
            borderRadius: 999,
            border: 'none',
            background: 'var(--color-surface-page, #f3f4f6)',
            color: 'var(--color-text-primary, #18181b)',
            cursor: 'pointer',
          }}
        >
          {activeItem.icon}
          <span>{activeItem.label}</span>
          <ChevronIcon />
        </button>

        {dropdownOpen && (
          <ul
            role="listbox"
            aria-label={labelTheme}
            style={{
              position: 'absolute',
              top: 'calc(100% + 4px)',
              right: 0,
              zIndex: 50,
              margin: 0,
              padding: 4,
              minWidth: 160,
              listStyle: 'none',
              borderRadius: 12,
              background: 'var(--color-surface-card, #fff)',
              boxShadow: '0 8px 24px rgba(0,0,0,0.12), 0 0 0 1px rgba(0,0,0,0.05)',
            }}
          >
            {items.map((item) => {
              const active = item.value === pref;
              return (
                <li key={item.value}>
                  <button
                    type="button"
                    role="option"
                    aria-selected={active}
                    onClick={() => {
                      choose(item.value);
                      setDropdownOpen(false);
                    }}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      width: '100%',
                      padding: '8px 10px',
                      border: 'none',
                      borderRadius: 8,
                      background: active ? 'var(--color-surface-page, #f3f4f6)' : 'transparent',
                      color: 'var(--color-text-primary, #18181b)',
                      fontSize: 13,
                      fontWeight: active ? 600 : 400,
                      textAlign: 'left',
                      cursor: 'pointer',
                    }}
                  >
                    {item.icon}
                    <span style={{ flex: 1 }}>{item.label}</span>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    );
  }

  return (
    <div
      role="group"
      aria-label={labelTheme}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 2,
        padding: 2,
        background: 'var(--color-surface-page, #f3f4f6)',
        borderRadius: 999,
      }}
    >
      <ThemeButton
        active={pref === 'light'}
        onClick={() => choose('light')}
        label={labelLight}
        icon={<SunIcon />}
      />
      <ThemeButton
        active={pref === 'dark'}
        onClick={() => choose('dark')}
        label={labelDark}
        icon={<MoonIcon />}
      />
      <ThemeButton
        active={pref === 'system'}
        onClick={() => choose('system')}
        label={labelSystem}
        icon={<MonitorIcon />}
      />
    </div>
  );
}

function ThemeButton({
  active,
  onClick,
  label,
  icon,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
  icon: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      aria-pressed={active}
      title={label}
      style={{
        // Round, tap-target-sized hit area. Active segment gets the
        // card surface for visual contrast against the group's
        // page-color background — subtle pill-on-pill, no heavy outlines.
        width: 28,
        height: 28,
        borderRadius: '50%',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: active ? 'var(--color-surface-card, #fff)' : 'transparent',
        color: active
          ? 'var(--color-text-primary, #18181b)'
          : 'var(--color-text-tertiary, #6b7280)',
        boxShadow: active ? '0 1px 2px rgba(0,0,0,0.06), 0 0 0 1px rgba(0,0,0,0.04)' : 'none',
        border: 'none',
        cursor: 'pointer',
        transition: 'background 150ms ease, color 150ms ease',
      }}
    >
      {icon}
    </button>
  );
}

// SVG icons. Stroked (not filled), inherit currentColor, sized 14px so
// they sit comfortably inside the 28px button. Lifted from Lucide's
// geometry; not Lucide-imported because this is a leaf client
// component and we don't want a runtime dep here.

function SunIcon({ size = 14, filled = false }: { size?: number; filled?: boolean }) {
  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <circle cx="12" cy="12" r="4" fill={filled ? 'currentColor' : 'none'} />
      <path d="M12 2v2" />
      <path d="M12 20v2" />
      <path d="m4.93 4.93 1.41 1.41" />
      <path d="m17.66 17.66 1.41 1.41" />
      <path d="M2 12h2" />
      <path d="M20 12h2" />
      <path d="m6.34 17.66-1.41 1.41" />
      <path d="m19.07 4.93-1.41 1.41" />
    </svg>
  );
}

function MoonIcon({ size = 14, filled = false }: { size?: number; filled?: boolean }) {
  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill={filled ? 'currentColor' : 'none'}
      stroke={filled ? 'none' : 'currentColor'}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M12 3a6 6 0 0 0 9 9 9 9 0 1 1-9-9z" />
    </svg>
  );
}

function MonitorIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      width="14"
      height="14"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <rect x="2" y="3" width="20" height="14" rx="2" ry="2" />
      <line x1="8" y1="21" x2="16" y2="21" />
      <line x1="12" y1="17" x2="12" y2="21" />
    </svg>
  );
}

function ChevronIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      width="12"
      height="12"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="m6 9 6 6 6-6" />
    </svg>
  );
}
